import React, { useState, useEffect, useCallback } from "react";
import { Search, Hand } from "lucide-react";
import { useTranslation } from "react-i18next";

const SECTION_COLORS = [
  { color: "#6C8EEF", bg: "rgba(108,142,239,0.10)" },
  { color: "#8B7FC7", bg: "rgba(139,127,199,0.10)" },
  { color: "#59C3E8", bg: "rgba(89,195,232,0.10)" },
  { color: "#E06CB0", bg: "rgba(224,108,176,0.10)" },
  { color: "#6C8EEF", bg: "rgba(108,142,239,0.10)" },
];

// Keys that signal "no specific city" — resolved from the location namespace
// so they stay in sync regardless of active language.
const NO_CITY_SENTINELS = new Set(['around_me', 'pick_from_map']);

const ServiceSelector = ({ isOpen, onClose, onSelect, selectedCity, isLocationBased = false }) => {
  const { t, i18n } = useTranslation('booking');
  const { t: tLoc } = useTranslation('location');
  const lang = i18n.language;
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const availableSections = [...new Set(services.map(s => s.section_name))];
  const sections = availableSections.map((sectionName, i) => {
    const svc = services.find(s => s.section_name === sectionName);
    const localizedLabel = (lang === 'en' && svc?.section_name_en) ||
                           (lang === 'ru' && svc?.section_name_ru) ||
                           sectionName;
    return {
      id: sectionName,
      label: localizedLabel.toLowerCase(),
      ...SECTION_COLORS[i % SECTION_COLORS.length],
    };
  });

  /**
   * Returns true when selectedCity should NOT be appended as a ?city= param.
   * Cases:
   *  - isLocationBased flag is set by parent (geo-based search)
   *  - selectedCity matches any translated sentinel ("Around me", "Около мен", etc.)
   *  - selectedCity is falsy
   */
  const isCityless = useCallback(() => {
    if (isLocationBased) return true;
    if (!selectedCity) return true;
    // Compare against translated values of both sentinels in all known namespaces
    const aroundMe = tLoc('around_me');
    const fromMap = tLoc('pick_from_map');
    return selectedCity === aroundMe || selectedCity === fromMap;
  }, [isLocationBased, selectedCity, tLoc]);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = import.meta.env.VITE_REACT_APP_API_URL;
      if (!apiUrl) throw new Error("API URL is not configured");
      let url = `${apiUrl}/api/cities/services`;
      if (!isCityless()) {
        url += `?city=${encodeURIComponent(selectedCity)}`;
      }
      const response = await fetch(url, { method: "GET", headers: { "Content-Type": "application/json" } });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data.success) setServices(data.data);
      else throw new Error(data.message || "Failed to fetch services");
    } catch (err) {
      console.error("Failed to fetch services:", err);
      setError(err.message);
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCity, isCityless]);

  useEffect(() => { if (isOpen) fetchServices(); }, [isOpen, selectedCity, fetchServices]);

  const filteredServices = services.filter(service => {
    // Filter out the generic catch-all service — check both the name and the
    // English marker so this works even when service names are localised server-side.
    if (service.name_en === "Other service") return false;
    const localizedName = (lang === 'en' && service.name_en) || (lang === 'ru' && service.name_ru) || service.name;
    const matchesSearch = localizedName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSection = selectedSection === "" || service.section_name === selectedSection;
    return matchesSearch && matchesSection;
  });

  if (!isOpen) return null;

  return (
    <div
      className="absolute top-full left-0 mt-2 z-50 w-80 rounded-2xl overflow-hidden"
      style={{
        background: "rgba(255,255,255,0.88)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        boxShadow: "0 16px 48px rgba(100,100,160,0.14)",
        border: "1px solid rgba(255,255,255,0.90)",
      }}
    >
      {/* Search input */}
      <div className="p-4" style={{ borderBottom: "1px solid rgba(108,142,239,0.12)" }}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#8A8AAA" }} />
          <input
            type="text"
            placeholder={t('service_selector_subtitle')}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm outline-none transition-all"
            style={{
              background: "rgba(232,236,247,0.70)",
              border: "1.5px solid transparent",
              color: "#2D2D4E",
            }}
            onFocus={e => { e.currentTarget.style.border = "1.5px solid rgba(108,142,239,0.40)"; e.currentTarget.style.background = "#fff"; }}
            onBlur={e => { e.currentTarget.style.border = "1.5px solid transparent"; e.currentTarget.style.background = "rgba(232,236,247,0.70)"; }}
          />
        </div>
      </div>

      {/* Section filters */}
      {sections.length > 0 && (
        <div className="px-4 py-3 flex flex-wrap gap-1.5" style={{ borderBottom: "1px solid rgba(108,142,239,0.12)" }}>
          {sections.map(s => {
            const isActive = selectedSection === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setSelectedSection(isActive ? "" : s.id)}
                className="px-3 py-1 rounded-full text-xs font-semibold transition-all duration-150"
                style={
                  isActive
                    ? { background: s.bg, color: s.color, border: `1.5px solid ${s.color}40` }
                    : { background: "rgba(232,236,247,0.70)", color: "#5A5A7A", border: "1.5px solid transparent" }
                }
                onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = s.bg; e.currentTarget.style.color = s.color; } }}
                onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = "rgba(232,236,247,0.70)"; e.currentTarget.style.color = "#5A5A7A"; } }}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Services list */}
      <div style={{ maxHeight: "256px", overflowY: "auto" }}>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div
              className="w-5 h-5 border-2 rounded-full animate-spin"
              style={{ borderColor: "rgba(108,142,239,0.15)", borderTopColor: "#6C8EEF" }}
            />
          </div>
        ) : error ? (
          <div className="text-center py-8 px-4">
            <p style={{ color: "#E06CB0", fontSize: "13px", marginBottom: "12px" }}>{error}</p>
            <button
              onClick={fetchServices}
              style={{ fontSize: "13px", fontWeight: 600, color: "#6C8EEF" }}
              className="hover:underline"
            >
              {t('retry', { ns: 'common' })}
            </button>
          </div>
        ) : filteredServices.length > 0 ? (
          <ul className="py-2">
            {filteredServices.map(s => {
              const sectionColor = sections.find(sec => sec.id === s.section_name);
              const iconColor = sectionColor?.color || "#6C8EEF";
              const iconBg = sectionColor?.bg || "rgba(108,142,239,0.10)";
              return (
                <li key={s.id}>
                  <button
                    onClick={() => { onSelect(s); onClose(); }}
                    className="w-full text-left px-4 py-3 flex items-center gap-3 transition-colors"
                    style={{ background: "transparent" }}
                    onMouseEnter={e => e.currentTarget.style.background = iconBg}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <div
                      className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: "rgba(232,236,247,0.80)" }}
                    >
                      <Hand className="w-4 h-4" style={{ color: iconColor }} />
                    </div>
                    <span style={{ fontSize: "13px", fontWeight: 500, color: "#2D2D4E" }}>
                      {(lang === 'en' && s.name_en) || (lang === 'ru' && s.name_ru) || s.name}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-center py-10" style={{ fontSize: "13px", color: "#8A8AAA" }}>
            {t('no_services_category', { ns: 'salon' })}
          </p>
        )}
      </div>
    </div>
  );
};

export default ServiceSelector;